import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

type Variant = 'primary' | 'ghost' | 'outline';
type Size = 'sm' | 'md' | 'lg';

interface NeonButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leading?: ReactNode;
  trailing?: ReactNode;
  fullWidth?: boolean;
}

const VARIANT: Record<Variant, string> = {
  // Brand: flat pink fill, white label, pink neon bloom — never black text on pink
  primary:
    'bg-pink text-white hover:bg-pink-dark active:bg-pink-deep shadow-glow-medium',
  ghost:
    'bg-transparent text-text-primary hover:bg-white/5 active:bg-white/10',
  outline:
    'bg-transparent text-text-primary border border-white/12 hover:border-pink hover:text-pink active:bg-pink/5',
};

const SIZE: Record<Size, string> = {
  sm: 'h-9 px-3 text-[11px]',
  md: 'h-11 px-5 text-[11px]',
  lg: 'h-14 px-7 text-xs',
};

/**
 * Neon button — brand v2.3: sharp 2px corners, Poppins 800 uppercase, CTA tracking +0.05em.
 * Active scales to 0.97 — no springy overshoot. No pill radii.
 */
export const NeonButton = forwardRef<HTMLButtonElement, NeonButtonProps>(
  (
    { variant = 'primary', size = 'md', leading, trailing, fullWidth, className, children, ...rest },
    ref,
  ) => (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-sharp font-display font-extrabold uppercase tracking-cta',
        'transition-[background,color,border-color,transform] duration-base ease-signal',
        'active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none',
        VARIANT[variant],
        SIZE[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {leading}
      {children}
      {trailing}
    </button>
  ),
);
NeonButton.displayName = 'NeonButton';
